import { HEAT, type HeatKey } from '../lib/course-heat';

// Category emblems, in the original 600x800 cover coordinate space (centered ~x300 / y330),
// reused verbatim from the first-version category covers Michael approved. The whole emblem
// group is nudged up by COVER.emblemShift to re-center it into this cover's image frame.
// `medical` is the Rod of Asclepius (single snake around a staff) — the universal medical mark.
const EMBLEMS: Record<string, JSX.Element> = {
  medical: (
    <g>
      {/* staff */}
      <path d="M300 250 L300 432" stroke="#ff9e7a" strokeWidth="11" strokeLinecap="round" fill="none" />
      <circle cx="300" cy="246" r="10" fill="#ff9e7a" />
      <path d="M288 432 h24" stroke="#ff9e7a" strokeWidth="11" strokeLinecap="round" />
      {/* snake head + body coiling around the staff */}
      <path
        d="M312 250 c8 -11 28 -9 33 4 c4 11 -5 23 -18 23 M316 272 C280 285 280 308 316 321 C352 334 352 357 316 370 C286 381 286 401 312 413"
        fill="none"
        stroke="#ff9e7a"
        strokeWidth="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="331" cy="258" r="3.6" fill="#331a14" />
    </g>
  ),
  military: (
    <g>
      <path d="M300 248 l24 52 56 6 -42 40 12 56 -50 -28 -50 28 12 -56 -42 -40 56 -6z" fill="#ff9e7a" />
      <path d="M230 410 l70 -22 70 22" stroke="#ff9e7a" strokeWidth="10" fill="none" strokeLinecap="round" />
    </g>
  ),
  law_enforcement: (
    <g>
      <path d="M300 244 l78 26 v70 c0 66 -44 104 -78 120 -34 -16 -78 -54 -78 -120 v-70z" stroke="#ff9e7a" strokeWidth="8" fill="none" />
      <path d="M300 300 l16 34 36 4 -27 26 7 36 -32 -18 -32 18 7 -36 -27 -26 36 -4z" fill="#ff9e7a" />
    </g>
  ),
  hospitality: (
    <g>
      <path d="M300 250 c-44 0 -72 30 -72 78 v44 h-16 v20 h176 v-20 h-16 v-44 c0 -48 -28 -78 -72 -78z" stroke="#ff9e7a" strokeWidth="8" fill="none" />
      <path d="M300 236 v16 M284 414 h32 v14 h-32z" stroke="#ff9e7a" strokeWidth="8" fill="#ff9e7a" strokeLinecap="round" />
    </g>
  ),
  aviation: (
    <g>
      <path d="M210 352 l180 -44 c14 -3 26 4 26 14 0 8 -6 14 -16 17 l-70 20 -44 86 -24 0 20 -80 -58 16 -20 26 -18 0 10 -40 -10 -40 18 0 20 26 58 16 -20 -80 24 0z" fill="#ff9e7a" />
    </g>
  ),
};

const DEFAULT_EMBLEM = (
  <g stroke="#ff9e7a" strokeWidth="8" fill="none">
    <rect x="232" y="252" width="136" height="156" rx="10" />
    <path d="M262 300 h76 M262 332 h76 M262 364 h50" strokeWidth="9" strokeLinecap="round" />
  </g>
);

function industryLabel(category: string): string {
  return category.replace(/_/g, ' ').toUpperCase();
}

// Shrink the title font so long language/course names stay on one line inside the safe width.
function fitFont(text: string, base: number, maxWidth: number, charFactor: number): number {
  const est = text.length * base * charFactor;
  return est <= maxWidth ? base : Math.max(base * 0.55, (maxWidth / (text.length * charFactor)));
}

export interface CourseCoverProps {
  category: string;
  targetLanguageName: string;
  courseTitle: string;
  heat: HeatKey;
  className?: string;
}

// A complete book-cover rendered as one 600x840 (5:7) SVG: a hot-gradient field, a centered
// GLOSSIKA wordmark + industry eyebrow, a glowing category emblem in a framed window, and a
// centered title block (Glossika <Language> / course / AUDIO COURSE pill). Because everything
// is SVG with text-anchor="middle", the cover scales crisply to any width, nothing wraps, and
// no text ever lands on the image frame.
export default function CourseCover({ category, targetLanguageName, courseTitle, heat, className }: CourseCoverProps) {
  const p = HEAT[heat];
  const emblem = EMBLEMS[category] ?? DEFAULT_EMBLEM;
  const titleTop = `Glossika ${targetLanguageName}`.trim();
  const titleFont = fitFont(titleTop, 56, 540, 0.5);
  const courseFont = fitFont(courseTitle, 30, 500, 0.46);
  const gid = `cover-bg-${heat}`;

  return (
    <svg
      viewBox="0 0 600 840"
      className={`block rounded-xl shrink-0 ${className ?? ''}`}
      style={{ height: 'auto' }}
      role="img"
      aria-label={`${titleTop} — ${courseTitle} audio course cover`}
    >
      <defs>
        <radialGradient id={gid} cx="50%" cy="32%" r="82%">
          <stop offset="0%" stopColor={p.stops[0]} />
          <stop offset="52%" stopColor={p.stops[1]} />
          <stop offset="100%" stopColor={p.stops[2]} />
        </radialGradient>
        <filter id="cover-glow" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="18" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <rect width="600" height="840" rx="18" fill={`url(#${gid})`} />
      <rect x="18" y="18" width="564" height="804" rx="16" fill="none" stroke={p.accent} strokeOpacity="0.18" strokeWidth="2" />

      {/* wordmark + industry eyebrow, centered in the top margin */}
      <text x="300" y="92" textAnchor="middle" fill={p.accent} fontSize="30" letterSpacing="8" fontWeight="700" fontFamily="Cormorant Garamond, Georgia, serif">
        GLOSSIKA
      </text>
      <text x="300" y="132" textAnchor="middle" fill="#f3efe6" fillOpacity="0.85" fontSize="21" letterSpacing="5" fontFamily="DM Sans, sans-serif">
        {industryLabel(category)}
      </text>

      {/* framed, glowing category emblem */}
      <rect x="160" y="178" width="280" height="280" rx="16" fill="#ffffff" fillOpacity="0.04" stroke={p.accent} strokeOpacity="0.26" strokeWidth="2" />
      <g filter="url(#cover-glow)" opacity="0.92" transform="translate(0,-20)" style={{ color: p.accent }}>
        {emblem}
      </g>

      {/* title block */}
      <text x="300" y="552" textAnchor="middle" fill="#f3efe6" fontSize={titleFont} fontWeight="600" fontFamily="Cormorant Garamond, Georgia, serif">
        {titleTop}
      </text>
      <text x="300" y="598" textAnchor="middle" fill={p.accent} fontSize={courseFont} fontFamily="Cormorant Garamond, Georgia, serif">
        {courseTitle}
      </text>
      <line x1="230" y1="634" x2="370" y2="634" stroke={p.accent} strokeOpacity="0.4" strokeWidth="1.5" />

      {/* AUDIO COURSE pill */}
      <rect x="205" y="668" width="190" height="46" rx="23" fill={p.badgeBg} />
      <text x="300" y="698" textAnchor="middle" fill={p.badgeText} fontSize="20" letterSpacing="4" fontWeight="700" fontFamily="DM Sans, sans-serif">
        AUDIO COURSE
      </text>
    </svg>
  );
}
